// TODO: How to handle Reprompting
// TODO: How to handle booting to DE
import { usePushdownContext } from '../Pushdown/PushdownProvider';
import { IMitekCaptureConfiguration, IMitekCaptureResult, SupportedDocumentTypes } from './MitekProvider';
import { clearSessionAndRedirectToDataEntry } from './MitekUtils';

type ErrorOptions = {
    // Mitek Props
    mitekDocumentType: SupportedDocumentTypes,
    dataEntryPageUrl?: string,
    errorGeneralPushdownCode?: string,
    errorSelfPushdownCode?: string,

    // Mitek Provider - startCapture
    captureConfiguration?: IMitekCaptureConfiguration,
    resultCallback?: (results: IMitekCaptureResult) => void,

    // Mitek Provider
    pushDownContext?: ReturnType<typeof usePushdownContext>,
}

type ErrorMapValue = {
    Code?: string,
    readonly Handler?: ((error?: string, options?: ErrorOptions) => void) | Function | null
}

export default function useMitekErrorHandling() {

    const failCallback = (error: string, callback?: (results: IMitekCaptureResult) => void, options?: ErrorOptions) => {
        try {
            callback && callback({
                documentType: options?.mitekDocumentType || 'UNKNOWN',
                errorMessage: error,
                successful: false,
            });
        } catch (callbackFailure) {
            console.error(callbackFailure);
        }
    };

    const UnsupportedDevice = (error: string, options: ErrorOptions) => {
        const { 
            dataEntryPageUrl,
            errorSelfPushdownCode, 
            pushDownContext,
            resultCallback, 
        } = options;

        failCallback(error, resultCallback);
        if (pushDownContext?.showPushdown) {
            pushDownContext?.showPushdown( errorSelfPushdownCode || 'apiErrorCode');
        }
        clearSessionAndRedirectToDataEntry(dataEntryPageUrl, 'MitekError');
    };
    
    const LogLicensingError = (error: string, options: ErrorOptions) => {
        const { 
            dataEntryPageUrl,
            errorSelfPushdownCode, 
            pushDownContext,
            resultCallback, 
        } = options;

        failCallback(error, resultCallback);
        if (pushDownContext?.showPushdown) {
            pushDownContext?.showPushdown(errorSelfPushdownCode || 'apiErrorCode');
        }
        console.error('An Error with Mitek has occurred');
        clearSessionAndRedirectToDataEntry(dataEntryPageUrl, 'MitekError');
    };
    
    const FallbackToManual = (error: string, options: ErrorOptions) => {
        const {
            dataEntryPageUrl,
            captureConfiguration,
            resultCallback, 
        } = options;
        if (captureConfiguration?.mode && captureConfiguration.mode !== 'MANUAL_CAPTURE') {
            captureConfiguration.mode = 'MANUAL_CAPTURE';
            (window as any).mitekScienceSDK.on('SDK_ERROR', ErrorHandlingHookGenerator({
                ...options,
                captureConfiguration,
            }));
            (window as any).mitekScienceSDK.cmd('CAPTURE_AND_PROCESS_FRAME', captureConfiguration);
        } else {
            failCallback(error, resultCallback);
            clearSessionAndRedirectToDataEntry(dataEntryPageUrl, 'MitekError');
        }
    };
    
    const ErrorMap: Record<string, ErrorMapValue> = {
        CAMERA_PERMISSION_DENIED: { // This device/browser camera-permission is denied
            Code: '113',
            Handler: UnsupportedDevice,
        },
        CAMERA_UNKNOWN_DEVICE_ISSUE: { // Camera was unable to start / Device issue
            Code: '130',
            Handler: UnsupportedDevice,
        },
        CONSTRAINT_NOT_SATISFIED: { // The user is likely using an unsupported device
            Code: '111', 
            Handler: UnsupportedDevice,
        },
        CORRUPT_IMAGE: { // Image provided was corrupt
            Code: '4002', 
            Handler: FallbackToManual, 
        },
        DEVICE_NOT_SUPPORTED: { // This device is not supported
            Code: '335', 
            Handler: UnsupportedDevice, 
        },
        DEVICE_PERMISSION_DENIED: { // Permission has been denied on device hardware or feature
            Code: '4113', 
            Handler: UnsupportedDevice,
        },
        INVALID_COMMAND_SIGNATURE: { // Incorrect number or type of arguments were passed to cmd
            Code: '332', 
            Handler: UnsupportedDevice,
        },
        INVALID_DOCUMENT_TYPE: {  // The document-type provided was not one enumerated in this doc
            Code: '400', 
            Handler: UnsupportedDevice, 
        },
        INVALID_BARCODE_LICENSE: { // No license was found for barcode
            Code: '4040', 
            Handler: LogLicensingError,
        },
        INVALID_IMAGE_DATA: { // Image data could not be extracted
            Code: '4004', 
            Handler: UnsupportedDevice, 
        },
        INVALID_STREAM_TRACK: { // Video-stream tracks were not found
            Code: '4005', 
            Handler: null, 
        },
        INVALID_LICENSE: { // License format is invalid
            Code: '4006', 
            Handler: LogLicensingError,
        },
        INVALID_LICENSE_EXPIRED: { // License is expired
            Code: '4007', 
            Handler: LogLicensingError,
        },
        INVALID_LICENSE_FEATURE: { // License does not include the intended feature
            Code: '4008', 
            Handler: LogLicensingError,
        },
        IMAGE_SMALLER_THAN_MIN_SIZE: { // Image provided did not meet minimum-dimensions 500x500
            Code: '4001', 
            Handler: UnsupportedDevice, 
        },
        MIC_UNKNOWN_DEVICE_ISSUE: { // Microphone or voice recording unknown device issue
            Code: '4009', 
            Handler: null,
        },
        MEDIA_RECORDER_NOT_SUPPORTED: { // This device/browser does not support MediaRecorder
            Code: '120',
            Handler: UnsupportedDevice,
        },
        NO_CAMERA_FOUND: { // No video camera is found
            Code: '112', 
            Handler: UnsupportedDevice,
        },
        OPTIONS_PARAMETER_INVALID_VALUE: { // The value of the parameter was out-of-bounds or invalid
            Code: '5004', 
            Handler: null,
        },
        PARAMETER_NOT_SET: { // A required parameter is missingaasxs
            Code: '5001',
            Handler: UnsupportedDevice,
        },
        PARAMETER_REQUIRED_DATA_NOT_PROVIDED: { // Part of the required parameter was missing
            Code: '5003', 
            Handler: null,
        },
        PARAMETER_TYPE_ERROR: { // The parameter’s type was incorrect
            Code: '5002', 
            Handler: null,
        },
        UNHANDLED_ERROR: { // Custom added for any non-listed errors.
            Code: 'Unknown',
            Handler: UnsupportedDevice,
        },
        UNKNOWN_ERROR: { // Unidentified exception error
            Code: '338', 
            Handler: UnsupportedDevice, 
        },
        UNKNOWN_METHOD: { // An unknown command method was called
            Code: '331', 
            Handler: null,
        },
        USER_MEDIA_NOT_SUPPORTED: { // This device/browser does not support getUserMedia=
            Code: '333', 
            Handler: UnsupportedDevice,
        },
        UNSUPPORTED_IMAGE_FORMAT: { // File provided was not a valid image format
            Code: '4003', 
            Handler: UnsupportedDevice, 
        },
        VIDEO_RECORDING_EXCEEDED_MAX_TIME: { // Max session time for videorecording was reached
            Code: '500', 
            Handler: null, 
        },
        WASM_NOT_SUPPORTED: { // This device/browser does not support WebAssembly
            Code: '334', 
            Handler: UnsupportedDevice, 
        },
        WEBGL_NOT_SUPPORTED: { // This device/browser does not support WebGL
            Code: '336', 
            Handler: UnsupportedDevice, 
        },
        WEBGL_SHADER_NOT_SUPPORTED: { // This device/browser does not support WebGL shader
            Code: '339', 
            Handler: UnsupportedDevice, 
        },
        FAKE_ERROR: { // For testing purposes only, should only be called in extreme circumstances
            
        },
    } as const;

    const ErrorHandlingHookGenerator = (
        options?: ErrorOptions,
    ) => (error: string) => {
        const errorHandler = ErrorMap[error]?.Handler ? ErrorMap[error] : ErrorMap.UNHANDLED_ERROR;
        if (errorHandler.Handler) {
            errorHandler.Handler(error, options);
            return;
        }
    };

    return {
        ErrorHandlingHookGenerator,
    };
}